import { Moon, Sun, BookOpen, WifiOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAdmin } from '../contexts/AdminContext';
import { usePlayer } from '../contexts/PlayerContext';
import { useOffline } from '../hooks/useOffline';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminLogin } from './AdminLogin';

const BASMALLAH_LABELS = {
  first: 'المقطع الأول فقط',
  all: 'قبل كل مقطع',
  none: 'معطّل'
};

export const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { isAdmin } = useAdmin();
  const { basmallahMode, setBasmallahMode } = usePlayer();
  const { isOffline } = useOffline();

  return (
    <motion.header 
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="sticky top-0 z-50 glass-strong"
    >
      <AnimatePresence>
        {isOffline && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-amber-500/90 text-amber-950 text-center text-sm py-1 px-4 flex items-center justify-center gap-2"
          >
            <WifiOff className="h-4 w-4" />
            <span>{t('offlineBanner')}</span>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo & Title - Right side (RTL) */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-800 to-emerald-950 border-2 border-amber-600/70 flex items-center justify-center shadow-lg shadow-emerald-900/30 shrink-0">
              <span className="font-arabic text-amber-500 text-[11px] leading-none font-bold" dir="rtl">القرآن<br/>الكريم</span>
            </div>
            <h1 className="font-arabic text-base sm:text-lg font-semibold text-foreground leading-tight" dir="rtl">
              مشروع حفظ الستين
            </h1>
          </div>

          {/* Controls - Left side (RTL) */}
          <div className="flex items-center gap-2">
            {/* Basmallah Settings - Admin Only */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`rounded-full gap-2 text-sm ${basmallahMode !== 'none' ? 'text-primary' : 'text-muted-foreground'}`}
                    data-testid="basmallah-settings-btn"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span className="hidden sm:inline">{BASMALLAH_LABELS[basmallahMode]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem 
                    onClick={() => setBasmallahMode('first')}
                    className={basmallahMode === 'first' ? 'bg-primary/10 text-primary' : ''}
                    data-testid="basmallah-first"
                  >
                    <BookOpen className="h-4 w-4 ml-2" />
                    المقطع الأول فقط
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setBasmallahMode('all')}
                    className={basmallahMode === 'all' ? 'bg-primary/10 text-primary' : ''}
                    data-testid="basmallah-all"
                  >
                    <BookOpen className="h-4 w-4 ml-2" />
                    قبل كل مقطع
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setBasmallahMode('none')}
                    className={basmallahMode === 'none' ? 'bg-primary/10 text-primary' : ''}
                    data-testid="basmallah-none"
                  >
                    <BookOpen className="h-4 w-4 ml-2" />
                    معطّل
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Admin Toggle */}
            <AdminLogin />

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full hover:bg-muted"
              data-testid="theme-toggle-btn"
            >
              <motion.div
                initial={false}
                animate={{ rotate: theme === 'dark' ? 0 : 180 }}
                transition={{ duration: 0.3 }}
              >
                {theme === 'dark' ? (
                  <Moon className="h-5 w-5" />
                ) : (
                  <Sun className="h-5 w-5" />
                )}
              </motion.div>
            </Button>
          </div>
        </div>
      </div>
    </motion.header>
  );
};
